import React, { useState, useEffect, useCallback } from 'react';
import {
  initSocket,
  subscribeToBalanceUpdateStatus,
  subscribeToSocketStatus
} from '../services/socketService';

const BalanceUpdateAlert = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [alerts, setAlerts] = useState([]);

  // Ensure the polling channel is active
  useEffect(() => {
    initSocket();
  }, []);

  // Monitor connection status from the polling channel shim
  useEffect(() => {
    const unsubscribe = subscribeToSocketStatus((status) => {
      setIsConnected(status === 'connected');
    });

    return () => unsubscribe();
  }, []);

  const dismissAlert = useCallback((alertId) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  }, []);

  const handleBalanceUpdateStatus = useCallback((data) => {
    let alertConfig = null;

    switch (data.type) {
      case 'daily_balance_save':
        if (data.status === 'success') {
          alertConfig = {
            id: Date.now(),
            type: 'success',
            title: 'Daily Balance Update Complete',
            message: `✅ Successfully updated ${data.success_count}/${data.total_accounts} accounts`,
            autoClose: 5000
          };
        } else if (data.status === 'partial_success') {
          alertConfig = {
            id: Date.now(),
            type: 'warning',
            title: 'Balance Update Completed with Issues',
            message: `⚠️ ${data.success_count}/${data.total_accounts} successful, ${data.failed_count} failed`,
            autoClose: 8000
          };
        } else if (data.status === 'failed') {
          alertConfig = {
            id: Date.now(),
            type: 'danger',
            title: 'Balance Update Failed',
            message: `❌ ${data.message}`,
            autoClose: 10000
          };
        }
        break;

      case 'balance_verification_alert':
        alertConfig = {
          id: Date.now(),
          type: 'warning',
          title: 'Balance Update Issue Detected',
          message: `⚠️ ${data.message}`,
          details: data.missing_accounts ? `Missing accounts: ${data.missing_accounts.join(', ')}` : null,
          autoClose: 8000
        };
        break;

      case 'balance_verification_info':
        // Light notification - just log for now
        console.log('ℹ️ Balance verification info:', data.message);
        break;

      case 'balance_verification_error':
        alertConfig = {
          id: Date.now(),
          type: 'danger',
          title: 'Balance System Error',
          message: `❌ ${data.message}`,
          details: data.error,
          autoClose: 10000
        };
        break;

      case 'balance_auto_healing_success':
        alertConfig = {
          id: Date.now(),
          type: 'success',
          title: 'Auto-Healing Successful',
          message: data.message,
          autoClose: 5000
        };
        break;

      case 'balance_partial_recovery':
        alertConfig = {
          id: Date.now(),
          type: data.status === 'warning' ? 'warning' : 'info',
          title: 'Partial Balance Recovery',
          message: data.message,
          details: data.remaining_failed ? `Still having issues: ${data.remaining_failed.join(', ')}` : null,
          autoClose: data.status === 'warning' ? 8000 : 6000
        };
        break;

      case 'balance_recovery_failed':
        alertConfig = {
          id: Date.now(),
          type: data.status === 'warning' ? 'warning' : 'info',
          title: 'Auto-Healing Status',
          message: data.message,
          details: data.failed_accounts ? `Affected accounts: ${data.failed_accounts.join(', ')}` : null,
          autoClose: data.status === 'warning' ? 8000 : 6000
        };
        break;

      case 'early_detection_success':
        alertConfig = {
          id: Date.now(),
          type: 'success',
          title: 'Early Detection & Fix',
          message: data.message,
          autoClose: 4000
        };
        break;

      case 'early_detection_warning':
        alertConfig = {
          id: Date.now(),
          type: 'info',
          title: 'Early Detection',
          message: data.message,
          autoClose: 5000
        };
        break;

      default:
        console.log('Unknown balance update status:', data);
    }

    if (alertConfig) {
      setAlerts(prev => [...prev, alertConfig]);

      // Auto close if specified
      if (alertConfig.autoClose) {
        setTimeout(() => {
          dismissAlert(alertConfig.id);
        }, alertConfig.autoClose);
      }
    }
  }, [dismissAlert]);

  // Listen for balance update notifications delivered via the shared channel
  useEffect(() => {
    const unsubscribe = subscribeToBalanceUpdateStatus((data) => {
      if (data) {
        console.log('📊 Balance update status received:', data);
        handleBalanceUpdateStatus(data);
      }
    });

    return () => unsubscribe();
  }, [handleBalanceUpdateStatus]);

  return (
    <div className="balance-update-alert">
      {/* Alert notifications */}
      <div style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 1050, maxWidth: '400px' }}>
        {alerts.map(alert => (
          <div key={alert.id} className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
            <strong>{alert.title}</strong>
            <br />
            {alert.message}
            {alert.details && (
              <>
                <br />
                <small className="text-muted">{alert.details}</small>
              </>
            )}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => dismissAlert(alert.id)}
              aria-label="Close"
            ></button>
          </div>
        ))}
      </div>

      {/* Connection status indicator */}
      {process.env.NODE_ENV === 'development' && (
        <div
          className="status-indicator-wrapper"
          style={{
            position: 'fixed',
            bottom: '10px',
            right: '10px',
            zIndex: 1001,
            cursor: 'pointer'
          }}
        >
          <style>{`
            .status-indicator-wrapper {
              position: relative;
            }
            .status-indicator-wrapper .tooltip-bubble {
              visibility: hidden;
              opacity: 0;
              position: absolute;
              bottom: calc(100% + 8px);
              right: 0;
              padding: 6px 12px;
              background-color: rgba(0, 0, 0, 0.85);
              color: white;
              font-size: 12px;
              white-space: nowrap;
              border-radius: 6px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              transition: opacity 0.3s ease, visibility 0.3s ease;
              pointer-events: none;
              z-index: 10000;
            }
            .status-indicator-wrapper .tooltip-bubble::after {
              content: "";
              position: absolute;
              top: 100%;
              right: 10px;
              border: 5px solid transparent;
              border-top-color: rgba(0, 0, 0, 0.85);
            }
            .status-indicator-wrapper:hover .tooltip-bubble {
              visibility: visible;
              opacity: 1;
            }
          `}</style>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            backgroundColor: isConnected ? '#28a745' : '#dc3545',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            transition: 'all 0.3s ease'
          }}>
            <span style={{ fontSize: '14px', filter: 'brightness(0) invert(1)' }}>📊</span>
          </div>
          <div className="tooltip-bubble">
            Balance Alerts: {isConnected ? 'Connected' : 'Disconnected'}
          </div>
        </div>
      )}
    </div>
  );
};

export default BalanceUpdateAlert;